import { Component, Input, ViewChild, ElementRef, AfterViewInit, EventEmitter, OnChanges, Output, SimpleChanges } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';

@Component({
	selector: 'ui-tabs',
	template: `
		<div class="d-flex justify-content-center">
			<div class="d-inline-block">
				<div class="tabs">
					<div class="sliding-bg" #slidingBg [style.transform]="transform" [style.width.px]="tabWidth"></div>
					<div *ngFor="let tab of tabs"
							class="tab"
							[class.active]="activeTab === tab.value"
							(click)="setActiveTab(tab.value, $event.currentTarget)">
						<span [innerHTML]="tab.label"></span>
					</div>
				</div>
			</div>
		</div>
	`,
	styles: [`
		.tabs {
			display: flex;
			background-color: #f1f1f1;
			padding: 5px;
			border-radius: 6px;
			position: relative;
		}

		.tab {
			padding: 5px 10px;
			cursor: pointer;
			border-radius: 6px;
			transition: background-color 0.3s, color 0.3s;
			margin-right: 5px;
			font-weight: bold;
		}

		.tab.active {
			background-color: white;
			font-weight: bold;
			z-index: 1;
			transition: background-color 0.3s, color 0.3s, transform 0.3s;
		}

		.tab:not(.active):hover {
			background-color: #EAEAEA;
		}

		.tab:last-child {
			margin-right: 0;
		}

		.sliding-bg {
			position: absolute;
			height: calc(100% - 10px);
			background-color: rgba(255,255,255, 0.9);
			transition: transform 0.3s ease-in-out, width 0.3s ease-in-out;
			border-radius: 6px;
			z-index: 0;
		}
	`]
})
export class UITabsComponent implements AfterViewInit, OnChanges {
	@Input() tabs: { label: string; value: string; content: any }[] = [];
	@Input() activeTab: string;
	@Output() tabSelected = new EventEmitter<string>();
	transform: string = 'translateX(0px)';
	tabWidth: number = 0;

	@ViewChild('slidingBg') slidingBg: ElementRef;

	ngAfterViewInit() {
		setTimeout(() => {
			this.updateSlidingBgByActiveTab();
		}, 0);
	}

	// Tabs that arrive later, or an active tab set by the caller, move the highlight too.
	ngOnChanges(changes: SimpleChanges) {
		if ((changes.activeTab || changes.tabs) && this.slidingBg) {
			setTimeout(() => this.updateSlidingBgByActiveTab(), 0);
		}
	}

	setActiveTab(tab: string, tabElement: HTMLElement) {
		this.activeTab = tab;
		this.updateSlidingBg(tabElement);
		this.tabSelected.emit(tab);
	}

	updateSlidingBg(tabElement: HTMLElement) {
		this.tabWidth = tabElement.offsetWidth - 10;
		const tabPosition = tabElement.offsetLeft;
		this.transform = `translateX(${tabPosition}px)`;
	}

	updateSlidingBgByActiveTab() {
		const activeTabElement = this.slidingBg.nativeElement.parentNode.querySelector('.tab.active');
		if (activeTabElement) {
			this.updateSlidingBg(activeTabElement as HTMLElement);
		}
	}
}

@Component({
	selector: 'ui-tab-content',
	template: `
		<div class="tab-content">
			<ng-container *ngFor="let tab of tabs">
				<ng-container *ngIf="activeTab === tab.value">
					<ng-container *ngTemplateOutlet="tab.content"></ng-container>
				</ng-container>
			</ng-container>
		</div>
	`,
	styles: [`
		.tab-content {
			transition: opacity 0.3s ease-in-out;
			position: relative;
			min-height: 100px;
		}
	`],
	animations: [
		trigger('tabTransition', [
			state('void', style({
				opacity: 0
			})),
			state('active', style({
				opacity: 1
			})),
			transition('void => active', [
				animate('0.3s ease-in-out')
			]),
			transition('active => void', [
				animate('0.3s ease-in-out')
			])
		])
	]
})
export class UITabContentComponent {
	@Input() tabs: { label: string; value: string; content: any }[] = [];
	@Input() activeTab: string;
}
